import { makeAutoObservable, runInAction } from 'mobx'
import { endOfMonth } from 'date-fns/endOfMonth'
import { endOfToday } from 'date-fns/endOfToday'
import { formatISO } from 'date-fns/formatISO'
import { getUnixTime } from 'date-fns/getUnixTime'
import { isBefore } from 'date-fns/isBefore'
import { startOfMonth } from 'date-fns/startOfMonth'
import { startOfToday } from 'date-fns/startOfToday'
import { subDays } from 'date-fns/subDays'

import { BeatApi, TransactionApi } from '../api'
import { TABLE_CONST } from '../constants/Table'
import { COLUMN_NAMES, DEFAULT_TRANSACTION } from '../constants/Transaction'
import { TransactionType } from '../types'
import type { Beat, Tag, Tagging, Transaction, TransactionRule } from '../types'
import {
  buildDetailedTransactions,
  flattenTransactionsWithSplits,
  sortAndFilterTransactions,
} from '../utils/TransactionUtils'

import { GlobalStore } from './GlobalStore'
import { upsertByGuid } from '../utils/upsertByGuid'

export type TransactionFilter = {
  accounts?: string[]
  dateRange?: { start: Date; end: Date }
  custom?: (value: Transaction) => boolean
  searchTerm?: string
  showSplits?: boolean
}

export class TransactionStore {
  globalStore: GlobalStore
  uiStore: UiStore
  api = new TransactionApi('/', '')
  beatApi = new BeatApi('/', '')

  associatedBeats: Beat[] = []
  associatedBeatsLoaded: boolean = false
  associatedBeatsLoading: boolean = false
  transactions: Transaction[] = []
  transactionsLoaded: boolean = false
  transactionsLoading: boolean = false
  taggings: Tagging[] = []
  taggingsLoaded: boolean = false
  taggingsLoading: boolean = false
  tags: Tag[] = []
  tagsLoaded: boolean = false
  tagsLoading: boolean = false
  transactionRules: TransactionRule[] = []
  transactionRulesLoaded: boolean = false
  transactionRulesLoading: boolean = false
  transactionSplitting: boolean = false

  cachedStartDate: Date = subDays(startOfToday(), 90)
  filter: TransactionFilter = {}
  isLoading = true

  alert = ''

  sortColumn = COLUMN_NAMES.DATE
  sortDirection = TABLE_CONST.DESC

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore
    this.uiStore = new UiStore()
    this.api = new TransactionApi(
      globalStore.endpoint,
      globalStore.sessionToken,
      globalStore.onError,
    )
    this.beatApi = new BeatApi(globalStore.endpoint, globalStore.sessionToken, globalStore.onError)

    makeAutoObservable(this)
  }

  loadTransactionData = async () => {
    if (!this.globalStore.accountStore.isAccountDataLoaded) {
      await this.globalStore.accountStore.loadAccountData()
    }
    if (!this.globalStore.categoryStore.categoriesLoaded) {
      await this.globalStore.categoryStore.loadCategories()
    }
    await Promise.all([
      this.loadAssociatedBeats(startOfMonth(new Date())),
      this.loadTags(),
      this.loadTaggings(),
      this.loadTransactionRules(),
      // By default loads 90 days of transactions
      this.loadTransactions(this.cachedStartDate),
    ])
  }

  get isTransactionDataLoaded(): boolean {
    return (
      this.associatedBeatsLoaded &&
      this.tagsLoaded &&
      this.taggingsLoaded &&
      this.transactionRulesLoaded &&
      this.transactionsLoaded
    )
  }

  get detailedTransactions(): Transaction[] {
    return this.isTransactionDataLoaded
      ? buildDetailedTransactions(
          this.transactions,
          this.globalStore.accountStore.visibleAccounts,
          this.globalStore.categoryStore.categories,
          this.taggings,
        )
      : []
  }

  get sortedTransactions(): Transaction[] {
    return sortAndFilterTransactions(
      this.filter,
      this.uiStore.expandedSplits,
      this.sortColumn,
      this.sortDirection,
      this.tags,
      this.detailedTransactions,
    )
  }

  get sortedTransactionsWithSplits(): Transaction[] {
    return flattenTransactionsWithSplits(this.sortedTransactions, this.detailedTransactions)
  }

  setAssociatedBeats = (beats: Beat[]) => {
    this.associatedBeats = beats
    this.associatedBeatsLoading = false
    this.associatedBeatsLoaded = true
  }

  setTaggings = (taggings: Tagging[]) => {
    this.taggings = taggings
    this.taggingsLoading = false
    this.taggingsLoaded = true
  }

  setTags = (tags: Tag[]) => {
    this.tags = tags
    this.tagsLoading = false
    this.tagsLoaded = true
  }

  setTransactionRules = (transactionRules: TransactionRule[]) => {
    this.transactionRules = transactionRules
    this.transactionRulesLoading = false
    this.transactionRulesLoaded = true
  }

  setTransactions = (transactions: Transaction[]) => {
    this.transactions = transactions
    this.transactionsLoading = false
    this.transactionsLoaded = true
  }

  setAlert = (alert: string) => {
    this.alert = alert
  }

  addTag = async (name: string) => {
    try {
      await this.api.addTag({ name } as Tag)
      await this.loadTags()
    } catch (error) {
      console.error('Error occurred while adding tag:', error)
      this.globalStore.captureException(error, { store: 'TransactionStore' })
    }
  }

  addTagging = async (tagGuid: string, transactionGuid: string) => {
    try {
      await this.api.addTagging({
        tag_guid: tagGuid,
        transaction_guid: transactionGuid,
      } as Tagging)
    } catch (error) {
      console.error('Error occurred while adding tagging:', error)
      this.globalStore.captureException(error, { store: 'TransactionStore' })
    }
  }

  addOrUpdateTransactionRule = async (categoryGuid: string, transaction: Transaction) => {
    try {
      const existingRule = this.transactionRules.find(
        (rule: TransactionRule) => rule.guid === transaction.user_transaction_rule_guid,
      ) as TransactionRule | undefined

      if (existingRule) {
        await this.api.updateTransactionRule({
          ...existingRule,
          category_guid: categoryGuid,
          description: transaction.description,
        } as TransactionRule)
      } else {
        await this.api.addTransactionRule({
          category_guid: categoryGuid,
          description: transaction.description,
          match_description: transaction.description,
        } as TransactionRule)
      }
      await this.loadTransactionRules()
    } catch (error) {
      console.error('Error occurred while adding/updating transaction rule:', error)
      this.globalStore.captureException(error, { store: 'TransactionStore' })
    }
  }

  addTransaction = async (transaction: Transaction) => {
    try {
      const newTransaction = await this.api.addTransaction(transaction)

      if (newTransaction) {
        runInAction(() => {
          this.setTransactions(upsertByGuid(this.transactions, newTransaction, true))

          // Brokaw may insert the transaction via WebSocket, but it never
          // updates the account balance — that only happens here.
          // See: money-dashboard/src/util/BrokawClient.ts::handleTransactionEvents (search "setTransactions")
          // If that handler ever calls updateAccountBalance, remove this call or guard on !existing.
          const balanceChange =
            newTransaction.transaction_type === TransactionType.CREDIT
              ? newTransaction.amount
              : -newTransaction.amount

          this.globalStore.accountStore.updateAccountBalance(
            newTransaction.account_guid,
            balanceChange,
          )
        })
      }
    } catch (error) {
      console.error('Error occurred while adding transaction:', error)
      this.globalStore.captureException(error, { store: 'TransactionStore' })
    }
  }

  loadAssociatedBeats = async (fromDate?: Date) => {
    if (this.associatedBeatsLoading) return

    this.associatedBeatsLoading = true
    const fromDateISO = fromDate ? formatISO(fromDate, { representation: 'date' }) : undefined
    try {
      const beats = await this.beatApi.getBeatsWithAssociatedTransaction(fromDateISO)
      this.setAssociatedBeats(beats)
    } catch (error) {
      console.error('Error occurred while loading associated beats:', error)
      this.globalStore.captureException(error, { store: 'TransactionStore' })
      this.setAssociatedBeats([])
    } finally {
      // Clear the in-flight flag even on failure so a later load (e.g. a widget
      // remount) can retry instead of being no-oped by the guard above, which would
      // leave isTransactionDataLoaded false and the list empty until a hard refresh.
      runInAction(() => {
        this.associatedBeatsLoading = false
      })
    }
  }

  loadTags = async () => {
    if (this.tagsLoading) return

    this.tagsLoading = true
    try {
      const tags = await this.api.getTags()
      this.setTags(tags)
    } catch (error) {
      console.error('Error occurred while loading tags:', error)
      this.globalStore.captureException(error, { store: 'TransactionStore' })
    } finally {
      runInAction(() => {
        this.tagsLoading = false
      })
    }
  }

  loadTaggings = async () => {
    if (this.taggingsLoading) return

    this.taggingsLoading = true
    try {
      const taggings = await this.api.getTaggings()
      this.setTaggings(taggings)
    } catch (error) {
      console.error('Error occurred while loading taggings:', error)
      this.globalStore.captureException(error, { store: 'TransactionStore' })
      this.setTaggings([])
    } finally {
      runInAction(() => {
        this.taggingsLoading = false
      })
    }
  }

  loadTransactionRules = async () => {
    if (this.transactionRulesLoading) return

    this.transactionRulesLoading = true
    try {
      const transactionRules = await this.api.getTransactionRules()
      this.setTransactionRules(transactionRules)
    } catch (error) {
      // Catch so a rejection can't abort the Promise.all in loadTransactionData.
      console.error('Error occurred while loading transaction rules:', error)
      this.globalStore.captureException(error, { store: 'TransactionStore' })
      this.setTransactionRules([])
    } finally {
      runInAction(() => {
        this.transactionRulesLoading = false
      })
    }
  }

  loadTransactions = async (startDate: Date = this.cachedStartDate) => {
    if (this.transactionsLoading) return

    this.setIsLoading(true)
    this.transactionsLoading = true

    try {
      const transactions = await this.api.getTransactionsByDateRange(
        getUnixTime(startDate),
        getUnixTime(endOfToday()),
      )
      this.setTransactions(transactions)
      // Advance the cache only after the range is actually loaded, so the invariant
      // "we hold transactions back to cachedStartDate" always holds.
      runInAction(() => {
        this.cachedStartDate = startDate
      })
    } catch (error) {
      console.error('Error occurred while loading transactions:', error)
      this.globalStore.captureException(error, { store: 'TransactionStore' })
    } finally {
      // See loadAssociatedBeats: clear the flag on failure so retries aren't no-oped.
      runInAction(() => {
        this.transactionsLoading = false
      })
      this.setIsLoading(false)
    }
  }

  modifyTransactionByAccountGuid = (guid: string, updates: Partial<Transaction>) => {
    this.transactions = this.transactions.map((txn) =>
      txn.account_guid === guid ? { ...txn, ...updates } : txn,
    )
  }

  removeTag = async (tagGuid: string) => {
    try {
      await this.api.removeTag(tagGuid)
      await this.loadTags()
    } catch (error) {
      console.error('Error occurred while removing tag:', error)
      this.globalStore.captureException(error, { store: 'TransactionStore' })
    }
  }

  removeTagging = async (taggingGuid: string) => {
    try {
      await this.api.removeTagging(taggingGuid)
    } catch (error) {
      console.error('Error occurred while removing tagging:', error)
      this.globalStore.captureException(error, { store: 'TransactionStore' })
    }
  }

  removeTransaction = async (transactionGuid: string) => {
    try {
      await this.api.removeTransaction(transactionGuid)

      const index = this.transactions.findIndex((t: Transaction) => t.guid === transactionGuid)

      if (index >= 0) {
        const updatedTransactions = [...this.transactions]
        updatedTransactions.splice(index, 1)
        this.setTransactions(updatedTransactions)
      }
    } catch (error) {
      console.error('Error occurred while removing transaction:', error)
      this.globalStore.captureException(error, { store: 'TransactionStore' })
    }
  }

  setFilter = (filter: TransactionFilter) => {
    this.filter = filter

    // Only reload if the new start date is prior to the cached start date; end date
    // is always today. loadTransactions advances cachedStartDate on success, so we
    // must not advance it here (a skipped/failed load would leave the list empty).
    if (filter.dateRange && isBefore(filter.dateRange.start, this.cachedStartDate)) {
      this.loadTransactions(filter.dateRange.start)
    }
  }

  setIsLoading = (isLoading: boolean) => (this.isLoading = isLoading)

  splitTransaction = async (transaction: Transaction, splits: Transaction[]) => {
    if (this.transactionSplitting) return
    this.transactionSplitting = true

    try {
      const updatedTransaction = await this.api.updateTransaction(transaction)
      const children = [] as Transaction[]

      for (let i = 0; i < splits.length; i++) {
        const child = await this.api.addTransaction(splits[i])
        children.push(child)
      }

      const index = this.transactions.findIndex((t: Transaction) => t.guid === transaction.guid)

      if (index >= 0) {
        // Remove old parent and any existing children
        const filtered = this.transactions.filter(
          (t) => t.guid !== transaction.guid && t.parent_guid !== transaction.guid,
        )

        // Insert updated parent + new children at original position
        this.setTransactions([
          ...filtered.slice(0, index),
          updatedTransaction,
          ...children,
          ...filtered.slice(index),
        ])
      }
    } catch (error) {
      console.error('Error occurred while splitting transaction:', error)
      this.globalStore.captureException(error, { store: 'TransactionStore' })
      throw error
    } finally {
      this.transactionSplitting = false
    }
  }

  unSplitTransaction = async (guid: string) => {
    try {
      const children = this.transactions.filter((t) => t.parent_guid === guid)

      await this.api.removeTransactionSplit(guid)

      // Remove children
      const transactions = [...this.transactions]
      children.forEach((child) => {
        const index = transactions.findIndex((t) => t.guid === child.guid)

        if (index >= 0) {
          transactions.splice(index, 1)
        }
      })

      // Update parent
      const index = transactions.findIndex((t) => t.guid === guid)

      runInAction(() => {
        const transaction = transactions[index]
        if (transaction) {
          transaction.has_been_split = false
        }

        if (index >= 0) {
          this.setTransactions([
            ...transactions.slice(0, index),
            transaction,
            ...transactions.slice(index + 1),
          ])
        }
      })
    } catch (error) {
      console.error('Error occurred while unsplitting transaction:', error)
      this.globalStore.captureException(error, { store: 'TransactionStore' })
    }
  }

  updateTaggings = async (tagGuids: string[], transactionGuid: string) => {
    try {
      const currentTaggings = this.taggings.filter((t) => t.transaction_guid === transactionGuid)

      const currentTagGuids = new Set(currentTaggings.map((t) => t.tag_guid))
      const incomingTagGuids = new Set(tagGuids)

      // Add new taggings that don't already exist
      for (const tagGuid of tagGuids) {
        if (!currentTagGuids.has(tagGuid)) {
          await this.addTagging(tagGuid, transactionGuid)
        }
      }

      // Remove taggings that are no longer needed
      for (const tagging of currentTaggings) {
        if (!incomingTagGuids.has(tagging.tag_guid)) {
          await this.removeTagging(tagging.guid)
        }
      }

      await this.loadTaggings()
    } catch (error) {
      console.error('Error occurred while updating taggings:', error)
      this.globalStore.captureException(error, { store: 'TransactionStore' })
    }
  }

  updateTag = async (tag: Tag) => {
    try {
      await this.api.updateTag(tag)
      await this.loadTags()
    } catch (error) {
      console.error('Error occurred while updating tag:', error)
      this.globalStore.captureException(error, { store: 'TransactionStore' })
    }
  }

  updateTransaction = async (transaction: Transaction) => {
    try {
      const updatedTransaction = await this.api.updateTransaction(transaction)

      const index = this.transactions.findIndex((t: Transaction) => t.guid === transaction.guid)

      if (index >= 0) {
        this.setTransactions([
          ...this.transactions.slice(0, index),
          updatedTransaction,
          ...this.transactions.slice(index + 1),
        ])
      }
    } catch (error) {
      console.error('Error occurred while updating transaction:', error)
      this.globalStore.captureException(error, { store: 'TransactionStore' })
    }
  }

  // This is used for loading the transactions list on demand, on clicking the different transactions of the insightful beats.
  loadEmbeddedInstanceTransactions = async (fromDate: Date) => {
    try {
      const transactions = await this.api.getTransactionsByDateRange(
        getUnixTime(fromDate),
        getUnixTime(endOfToday()),
      )
      this.cachedStartDate = fromDate
      this.transactions = transactions
    } catch (error) {
      console.error('Error occurred while loading embedded instance transactions:', error)
      this.globalStore.captureException(error, { store: 'TransactionStore' })
    }
  }
}

export class UiStore {
  beginDate = startOfMonth(new Date())
  endDate = endOfMonth(new Date())

  expandedSplits = [] as string[]
  height = 550
  width = 1000
  shouldDisableDrawerScroll = false

  searchValue = ''
  selectedAccountGuids = [] as string[]

  manualTransaction: Transaction = DEFAULT_TRANSACTION
  isAmountValid = false

  showInsights = false

  constructor() {
    makeAutoObservable(this)
  }

  setDateRange = (beginDate: Date, endDate: Date) => {
    this.beginDate = beginDate
    this.endDate = endDate
  }

  setIsAmountValid = (valid: boolean) => (this.isAmountValid = valid)

  setSearchValue = (searchValue: string) => (this.searchValue = searchValue)

  setSelectedAccountGuids = (accountGuids: string[]) => (this.selectedAccountGuids = accountGuids)

  setShouldDisableDrawerScroll = (shouldDisable: boolean) =>
    (this.shouldDisableDrawerScroll = shouldDisable)

  setShowInsights = (showInsights: boolean) => {
    this.showInsights = showInsights
  }

  setSize = (height: number, width: number) => {
    this.height = height
    this.width = width
  }

  toggleSplit = (guid: string) => {
    const expandedSplits = [...this.expandedSplits]
    const index = this.expandedSplits.findIndex((splitGuid) => splitGuid === guid)

    if (index >= 0) {
      expandedSplits.splice(index, 1)
    } else {
      expandedSplits.push(guid)
    }

    this.expandedSplits = expandedSplits
  }

  clearExpandedSplits = () => {
    if (this.expandedSplits.length === 0) return
    this.expandedSplits = []
  }

  updateManualTransaction = (manualTransaction: Transaction) => {
    this.manualTransaction = manualTransaction
  }
}
